import { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { publicSupabase as supabase } from '../lib/supabase';
import { gsap } from 'gsap';
import MediaPicker from '../components/public/MediaPicker';
import { uploadMedia } from '../lib/publicMedia';

// Auto-gradient partner for the survey theme color
const darkenHex = (hex, f = 0.22) => {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || '');
  if (!m) return hex;
  const n = parseInt(m[1], 16);
  const d = (c) => Math.max(0, Math.round(c * (1 - f)));
  return `#${(((d((n >> 16) & 255)) << 16) | ((d((n >> 8) & 255)) << 8) | d(n & 255)).toString(16).padStart(6, '0')}`;
};

// survey_responses.employee_id is a uuid FK — only forward genuine UUIDs.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Resolution (code lookup, expiry, scan count, password) belongs to
// PublicForm — the one front door both public paths come through. This
// component renders a survey it has already been handed.
export default function Survey({ survey, qrLink }) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [answers, setAnswers] = useState({});
  const [zoomSrc, setZoomSrc] = useState(null);   // question picture shown full screen
  // Files chosen for "photo" questions, keyed by question id. Held locally and
  // uploaded on submit, so an abandoned form leaves nothing in the bucket.
  const [photoFiles, setPhotoFiles] = useState({});
  const [uploadProgress, setUploadProgress] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [employeeId, setEmployeeId] = useState('');       // resolved UUID
  const [respName, setRespName] = useState('');           // chosen display name
  const [typedName, setTypedName] = useState('');         // fallback: name typed by someone without an account
  const [respQuery, setRespQuery] = useState('');         // search box text
  const [respResults, setRespResults] = useState([]);
  const [respSearching, setRespSearching] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const whoRef = useRef(null);                            // "Who are you?" box, scrolled to when a name is missing

  // Respondent name search (non-anonymous) — branch-scoped RPC, debounced
  useEffect(() => {
    const q = respQuery.trim();
    if (employeeId || typedName || q.length < 2) { setRespResults([]); return; }
    let cancelled = false;
    setRespSearching(true);
    const h = setTimeout(async () => {
      const { data, error } = await supabase.rpc('search_survey_respondent', {
        p_branch_id: qrLink?.branch_id || null,
        p_query: q,
      });
      if (!cancelled) {
        setRespResults(error ? [] : (data || []));
        setRespSearching(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(h); };
  }, [respQuery, employeeId, typedName, qrLink]);

  // Entrance: question cards rise in with a soft stagger (GSAP)
  useEffect(() => {
    if (!survey) return;
    const cards = document.querySelectorAll('.survey-q');
    if (cards.length === 0) return;
    gsap.fromTo(cards, { y: 18, opacity: 0 }, { y: 0, opacity: 1, duration: 0.45, stagger: 0.07, ease: 'power2.out' });
  }, [survey]);

  // Non-empty answer (arrays = checkboxes / photos)
  const isAnswered = (a) => (Array.isArray(a) ? a.length > 0 : (!!a || a === 0));

  const handleAnswerChange = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');

    // Check required questions. A photo answer lives in photoFiles until
    // submit, so "answered" for that type means at least one file chosen.
    const requiredQuestions = survey.questions.filter(q => q.required);
    const unanswered = requiredQuestions.filter(q => (
      q.type === 'photo'
        ? (photoFiles[q.id] || []).length === 0
        : Array.isArray(answers[q.id])
          ? answers[q.id].length === 0
          : !answers[q.id] && answers[q.id] !== 0
    ));
    if (unanswered.length > 0) {
      setSubmitError(t('survey.requiredMissing', { count: unanswered.length }));
      return;
    }
    // A required explanation ("why?") counts as part of the answer
    const missingWhy = survey.questions.filter(q => (
      q.explain && q.explain_required && isAnswered(answers[q.id]) && !String(answers[`${q.id}_why`] || '').trim()
    ));
    if (missingWhy.length > 0) {
      setSubmitError(t('survey.explainMissing', { defaultValue: 'Please explain your answer for: {{q}}', q: missingWhy[0].question }));
      return;
    }

    // A non-anonymous survey needs a name, otherwise the answer lands as
    // "Anonymous" in HR's results — exactly what happened with the first
    // Suci internet survey, where nobody could find themselves and the
    // form still let them send.
    if (!survey.is_anonymous && !(employeeId && UUID_RE.test(employeeId)) && !typedName.trim()) {
      setSubmitError(t('survey.whoRequired', 'Please pick your name first so your answer is recorded to you.'));
      whoRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    setSubmitting(true);
    try {
      // Upload every photo question's files, then fold the returned
      // descriptors into the answers jsonb. Nothing reaches the bucket until
      // the guest actually submits.
      const pending = Object.entries(photoFiles).filter(([, f]) => f && f.length > 0);
      let payload = answers;
      if (pending.length > 0) {
        const total = pending.reduce((n, [, f]) => n + f.length, 0);
        let done = 0;
        const withMedia = { ...answers };
        for (const [qid, f] of pending) {
          withMedia[qid] = await uploadMedia(f, () => {
            done += 1;
            setUploadProgress(`${done}/${total}`);
          });
        }
        payload = withMedia;
      }

      // employeeId is a resolved profile UUID (from the name picker) or ''.
      const empIdUuid = employeeId && UUID_RE.test(employeeId) ? employeeId : null;

      // survey_responses is not writable by anon — submissions go through
      // the validating submit_public_survey RPC (active-survey check,
      // employee verification, size cap).
      const rpcArgs = {
        p_survey_id: survey.id,
        p_answers: payload,
        p_employee_id: survey.is_anonymous ? null : empIdUuid,
        p_branch_id: qrLink?.branch_id || null,
        // Which printed QR this came from ('suci', 'arcamanik'). Set on the
        // QR row by HR, so it cannot be forged from the URL.
        p_source: qrLink?.source || null,
        // Staff without an account: the name they typed, kept by the RPC only
        // when no profile matched and the survey is not anonymous.
        p_respondent_name: survey.is_anonymous || empIdUuid ? null : (typedName.trim() || null),
      };
      let { data: result, error } = await supabase.rpc('submit_public_survey', rpcArgs);
      // Safety net: until the respondent_name migration is applied, the
      // database only knows the previous 5-argument function and PostgREST
      // answers PGRST202. Resend without the typed name rather than lose
      // the whole answer.
      if (error && error.code === 'PGRST202') {
        const { p_respondent_name, ...legacyArgs } = rpcArgs;
        ({ data: result, error } = await supabase.rpc('submit_public_survey', legacyArgs));
      }
      if (!error && result && result.success === false) {
        throw new Error(result.error || 'submit_failed');
      }
      if (error) throw error;
      setSubmitted(true);
    } catch (err) {
      console.error('Error submitting survey:', err);
      setSubmitError(err?.message || t('survey.submitFailed'));
    } finally {
      setSubmitting(false);
      setUploadProgress('');
    }
  };

  const renderQuestionInput = (question) => {
    const value = answers[question.id];

    // Options with pictures (set in the survey builder): a grid of cards —
    // picture on top, label below; 🔍 opens the picture full screen. The
    // answer is still the option text, exactly like the plain list.
    if ((question.type === 'multiple_choice' || question.type === 'checkboxes') && (question.option_images || []).some(Boolean)) {
      const multi = question.type === 'checkboxes';
      const picked = multi ? (Array.isArray(value) ? value : []) : [];
      return (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
          {question.options?.map((opt, idx) => {
            const img = question.option_images[idx];
            const on = multi ? picked.includes(opt) : value === opt;
            return (
              <div
                key={idx}
                role="button"
                tabIndex={0}
                onClick={() => handleAnswerChange(question.id, multi ? (on ? picked.filter(o => o !== opt) : [...picked, opt]) : opt)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.currentTarget.click(); } }}
                className={`relative rounded-xl border-2 overflow-hidden bg-white cursor-pointer transition-all ${on ? 'border-amber-500 ring-2 ring-amber-200' : 'border-gray-200 hover:border-gray-300'}`}
              >
                <div className="aspect-square bg-gray-50 flex items-center justify-center">
                  {img ? <img src={img} alt="" loading="lazy" className="w-full h-full object-contain" /> : <span className="text-3xl text-gray-300">🖼️</span>}
                </div>
                {img && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setZoomSrc(img); }}
                    aria-label={t('survey.tapToZoom', 'Tap to enlarge')}
                    className="absolute top-1.5 right-1.5 w-8 h-8 rounded-full bg-black/55 text-white text-sm flex items-center justify-center"
                  >🔍</button>
                )}
                {on && <span className="absolute top-1.5 left-1.5 w-7 h-7 rounded-full bg-amber-500 text-white font-bold flex items-center justify-center">✓</span>}
                <div className={`px-2 py-2 text-sm font-semibold text-center ${on ? 'text-amber-700' : 'text-gray-900'}`}>{opt}</div>
              </div>
            );
          })}
        </div>
      );
    }

    switch (question.type) {
      case 'rating':
        return (
          <div>
            <div className="grid grid-cols-5 gap-2 sm:gap-3">
              {[1, 2, 3, 4, 5].map(n => (
                <button
                  key={n}
                  type="button"
                  onClick={(e) => {
                    handleAnswerChange(question.id, n);
                    // GSAP: springy pop on the tapped card + star spin-pop
                    const card = e.currentTarget;
                    gsap.fromTo(card, { scale: 0.82 }, { scale: 1, duration: 0.5, ease: 'back.out(2.8)' });
                    const star = card.querySelector('.star-i');
                    if (star) gsap.fromTo(star, { scale: 0.4, rotate: -30 }, { scale: 1, rotate: 0, duration: 0.55, ease: 'elastic.out(1, 0.45)' });
                  }}
                  className={`aspect-square rounded-2xl border-2 flex flex-col items-center justify-center gap-1 transition-colors ${
                    value === n
                      ? 'border-amber-400 bg-amber-50'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                  style={value === n && survey.theme_color ? { borderColor: survey.theme_color, background: `${survey.theme_color}14` } : undefined}
                >
                  <span className={`font-bold text-lg sm:text-xl ${value === n ? 'text-amber-600' : 'text-gray-900'}`}>{n}</span>
                  <svg className="star-i w-6 h-6 sm:w-7 sm:h-7" viewBox="0 0 24 24"
                    fill={value != null && n <= value ? '#f59e0b' : '#d1d5db'}>
                    <path d="M12 2l2.9 6.26 6.87.72-5.13 4.62 1.43 6.75L12 16.9l-6.07 3.45 1.43-6.75-5.13-4.62 6.87-.72L12 2z" />
                  </svg>
                </button>
              ))}
            </div>
            {(question.min_label || question.max_label) && (
              <div className="flex justify-between items-center mt-3 text-sm sm:text-base">
                <span className="flex items-center gap-2 text-gray-700">
                  {question.min_label && <><span className="text-red-500 text-lg">☹️</span>{question.min_label}</>}
                </span>
                <span className="flex items-center gap-2 text-gray-700">
                  {question.max_label && <>{question.max_label}<span className="text-green-500 text-lg">🙂</span></>}
                </span>
              </div>
            )}
          </div>
        );

      case 'scale_10':
        return (
          <div>
          <div className="flex gap-1 justify-center flex-wrap">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => (
              <button
                key={n}
                type="button"
                onClick={(e) => {
                  handleAnswerChange(question.id, n);
                  gsap.fromTo(e.currentTarget, { scale: 0.82 }, { scale: 1, duration: 0.4, ease: 'back.out(2.5)' });
                }}
                className={`w-10 h-10 rounded-lg border-2 font-medium transition-all ${
                  value === n
                    ? 'border-amber-500 bg-amber-50 text-amber-600'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                {n}
              </button>
            ))}
          </div>
          {(question.min_label || question.max_label) && (
            <div className="flex justify-between items-center mt-3 text-sm">
              <span className="flex items-center gap-2 text-gray-700">
                {question.min_label && <><span className="text-red-500">☹️</span>{question.min_label}</>}
              </span>
              <span className="flex items-center gap-2 text-gray-700">
                {question.max_label && <>{question.max_label}<span className="text-green-500">🙂</span></>}
              </span>
            </div>
          )}
          </div>
        );

      case 'yes_no':
        return (
          <div className="flex gap-3 justify-center">
            {['Yes', 'No'].map(opt => (
              <button
                key={opt}
                type="button"
                onClick={() => handleAnswerChange(question.id, opt)}
                className={`px-8 py-3 rounded-xl border-2 font-medium transition-all ${
                  value === opt
                    ? 'border-amber-500 bg-amber-50 text-amber-600'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                {opt}
              </button>
            ))}
          </div>
        );

      case 'multiple_choice':
        return (
          <div className="space-y-2">
            {question.options?.map((opt, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => handleAnswerChange(question.id, opt)}
                className={`w-full p-3 rounded-xl border-2 text-left transition-all ${
                  value === opt
                    ? 'border-amber-500 bg-amber-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                {opt}
              </button>
            ))}
          </div>
        );

      case 'checkboxes': {
        // Several options may be ticked; the answer is the array of picks
        const picked = Array.isArray(value) ? value : [];
        return (
          <div className="space-y-2">
            {question.options?.map((opt, idx) => {
              const on = picked.includes(opt);
              return (
                <button
                  key={idx}
                  type="button"
                  onClick={() => handleAnswerChange(question.id, on ? picked.filter(o => o !== opt) : [...picked, opt])}
                  className={`w-full p-3 rounded-xl border-2 text-left transition-all flex items-center gap-3 ${
                    on
                      ? 'border-amber-500 bg-amber-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <span className={`w-5 h-5 rounded flex items-center justify-center text-xs flex-shrink-0 border-2 ${on ? 'bg-amber-500 border-amber-500 text-white' : 'border-gray-300 bg-white'}`}>{on ? '✓' : ''}</span>
                  <span>{opt}</span>
                </button>
              );
            })}
          </div>
        );
      }

      case 'photo':
        return (
          <MediaPicker
            files={photoFiles[question.id] || []}
            onChange={(next) => setPhotoFiles((prev) => ({ ...prev, [question.id]: next }))}
            compact
          />
        );

      case 'text':
      default:
        return (
          <textarea
            value={value || ''}
            onChange={(e) => handleAnswerChange(question.id, e.target.value)}
            rows={3}
            className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
            placeholder={t('survey.typeAnswer')}
          />
        );
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
          <div className="text-6xl mb-4">✅</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('survey.thankYouTitle')}</h1>
          <p className="text-gray-600 mb-6">
            {t('survey.thankYouBody')}
          </p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700"
          >
            {t('survey.done')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          {/* Header */}
          {survey.banner_url && (
            <img src={survey.banner_url} alt="" className="w-full h-40 sm:h-52 object-cover" />
          )}
          <div
            className="bg-gradient-to-r from-amber-500 to-amber-600 p-6 text-white"
            style={survey.theme_color ? { background: `linear-gradient(to right, ${survey.theme_color}, ${darkenHex(survey.theme_color)})` } : undefined}
          >
            <h1 className="text-2xl font-bold">{survey.title}</h1>
            {survey.description && (
              <p className="text-amber-100 mt-2">{survey.description}</p>
            )}
            <div className="mt-3 flex gap-2">
              {survey.is_anonymous && (
                <span className="inline-flex items-center px-3 py-1 bg-white/20 rounded-full text-sm">
                  🔒 {t('survey.anonymousBadge')}
                </span>
              )}
              <span className="inline-flex items-center px-3 py-1 bg-white/20 rounded-full text-sm">
                {t('survey.questionsCount', { count: survey.questions?.length || 0 })}
              </span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="p-6">
            {/* Non-anonymous: pick your NAME (branch-scoped lookup) instead of
                typing a UUID nobody knows. */}
            {!survey.is_anonymous && (
              <div ref={whoRef} className="mb-6 p-4 bg-blue-50 rounded-xl">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('survey.whoLabel', 'Who are you?')}
                </label>
                {(employeeId || typedName) ? (
                  <div className="flex items-center justify-between bg-white border border-green-300 rounded-xl px-4 py-3">
                    <span className="font-medium text-gray-800">✓ {respName}</span>
                    <button
                      type="button"
                      onClick={() => { setEmployeeId(''); setTypedName(''); setRespName(''); setRespQuery(''); setRespResults([]); }}
                      className="text-sm text-gray-500 hover:text-gray-700"
                    >
                      {t('survey.change', 'Change')}
                    </button>
                  </div>
                ) : (
                  <div className="relative">
                    <input
                      type="text"
                      value={respQuery}
                      onChange={(e) => setRespQuery(e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500"
                      placeholder={t('survey.whoPlaceholder', 'Type your name to find yourself…')}
                    />
                    {/* No account? Keep the typed name instead of losing it. */}
                    {!respSearching && respResults.length === 0 && respQuery.trim().length >= 2 && (
                      <button
                        type="button"
                        onClick={() => { const n = respQuery.trim(); setTypedName(n); setRespName(n); setRespResults([]); }}
                        className="mt-2 w-full px-4 py-3 text-left text-sm bg-white border border-dashed border-amber-400 rounded-xl hover:bg-amber-50"
                      >
                        {t('survey.whoUseTyped', 'Not listed? Continue as "{{name}}"', { name: respQuery.trim() })}
                      </button>
                    )}
                    {(respResults.length > 0 || respSearching) && (
                      <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-auto">
                        {respSearching && respResults.length === 0 ? (
                          <div className="px-4 py-3 text-sm text-gray-400">{t('survey.searching', 'Searching…')}</div>
                        ) : (
                          respResults.map((r) => (
                            <button
                              key={r.id}
                              type="button"
                              onClick={() => { setEmployeeId(r.id); setRespName(r.full_name); setRespResults([]); }}
                              className="flex items-center justify-between w-full px-4 py-3 text-left hover:bg-amber-50"
                            >
                              <span className="font-medium text-gray-800">{r.full_name}</span>
                              {r.phone_hint && <span className="text-xs text-gray-400">{r.phone_hint}</span>}
                            </button>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                )}
                <p className="text-xs text-gray-500 mt-2">
                  {t('survey.whoHint', 'This survey is not anonymous. Pick your name so your answer is recorded to you. Not listed? Type your full name and continue.')}
                </p>
              </div>
            )}

            {/* Questions */}
            <div className="space-y-6">
              {survey.questions?.map((question, idx) => (
                <div key={question.id} className="survey-q p-4 bg-gray-50 rounded-xl">
                  <div className="mb-4">
                    <span className="text-sm text-amber-600 font-medium">{t('survey.questionLabel', { number: idx + 1 })}</span>
                    <h3 className="text-lg font-medium text-gray-900 mt-1">
                      {question.question}
                      {question.required && <span className="text-red-500 ml-1">*</span>}
                    </h3>
                    {question.scale_hint && (
                      <p className="text-sm text-gray-500 italic mt-1">{question.scale_hint}</p>
                    )}
                    {question.image_url && (
                      <button
                        type="button"
                        onClick={() => setZoomSrc(question.image_url)}
                        className="mt-3 relative block w-full rounded-lg border border-gray-200 bg-white overflow-hidden cursor-zoom-in"
                        aria-label={t('survey.tapToZoom', 'Tap to enlarge')}
                      >
                        <img src={question.image_url} alt="" className="block w-full max-h-[28rem] object-contain" />
                        <span className="absolute right-2 bottom-2 rounded-full bg-black/60 text-white text-xs font-semibold px-2.5 py-1">
                          ⤢ {t('survey.tapToZoom', 'Tap to enlarge')}
                        </span>
                      </button>
                    )}
                  </div>
                  {renderQuestionInput(question)}
                  {question.explain && question.type !== 'text' && question.type !== 'photo' && isAnswered(answers[question.id]) && (
                    <div className="mt-3">
                      <label className="block text-sm text-gray-700 mb-1">
                        💬 {question.explain_label || t('survey.explainDefault', 'Why?')}
                        {question.explain_required && <span className="text-red-500 ml-1">*</span>}
                      </label>
                      <textarea
                        rows={2}
                        value={answers[`${question.id}_why`] || ''}
                        onChange={(e) => handleAnswerChange(`${question.id}_why`, e.target.value)}
                        placeholder={t('survey.explainPlaceholder', 'Tell us more…')}
                        className="w-full p-3 rounded-xl border-2 border-gray-200 focus:border-amber-500 focus:outline-none resize-y"
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Anonymity reassurance right before submitting */}
            {survey.is_anonymous && (
              <div className="mt-8 pt-5 border-t border-gray-200 flex items-center gap-3">
                <span className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-gray-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="4" y="11" width="16" height="10" rx="2" />
                    <path d="M8 11V7a4 4 0 118 0v4" />
                  </svg>
                </span>
                <p className="text-gray-600 text-sm sm:text-base">
                  {t('survey.anonymousFooter', 'Jawaban Anda anonim dan tidak akan dikaitkan dengan identitas Anda.')}
                </p>
              </div>
            )}

            {/* Submit */}
            <div className="mt-8">
              {submitError && (
                <p className="text-sm text-red-600 text-center mb-3" role="alert">{submitError}</p>
              )}
              <button
                type="submit"
                disabled={submitting}
                className="w-full py-4 bg-amber-600 text-white font-semibold rounded-xl hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                style={survey.theme_color ? { background: `linear-gradient(to right, ${survey.theme_color}, ${darkenHex(survey.theme_color)})` } : undefined}
              >
                {submitting
                  ? (uploadProgress ? t('survey.uploading', { progress: uploadProgress }) : t('survey.submitting'))
                  : t('survey.submit')}
              </button>
            </div>

            {survey.is_anonymous && (
              <p className="text-center text-xs text-gray-500 mt-4">
                {t('survey.anonymousNote')}
              </p>
            )}
          </form>
        </div>
      </div>
      {zoomSrc && <ImageZoom src={zoomSrc} onClose={() => setZoomSrc(null)} t={t} />}
    </div>
  );
}

// Full-screen picture viewer (question picture + option pictures):
// double-tap / double-click zooms in where you tapped (again = back out),
// pinch zooms around the fingers (1–5×), drag pans when zoomed (kept inside
// the picture), mouse wheel zooms toward the pointer; tap the dark area,
// ✕, Esc or swipe down (not zoomed) to close.
function ImageZoom({ src, onClose, t }) {
  const MAX = 5;
  const boxRef = useRef(null);
  const imgRef = useRef(null);
  const [view, setView] = useState({ s: 1, x: 0, y: 0 });
  const [anim, setAnim] = useState(false);
  const v = useRef(view);
  v.current = view;
  const pointers = useRef(new Map());
  const gesture = useRef(null);
  const lastTap = useRef({ t: 0, x: 0, y: 0 });

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [onClose]);

  // keep the zoomed picture covering the screen (no empty edges dragged in)
  const clamp = (s, x, y) => {
    const img = imgRef.current; const box = boxRef.current;
    if (!img || !box) return { s, x, y };
    const w = img.offsetWidth * s; const h = img.offsetHeight * s;
    const mx = Math.max(0, (w - box.clientWidth) / 2); const my = Math.max(0, (h - box.clientHeight) / 2);
    return { s, x: Math.min(mx, Math.max(-mx, x)), y: Math.min(my, Math.max(-my, y)) };
  };
  // zoom to scale s2 keeping the screen point (px, py) still
  const zoomAt = (s2, px, py, smooth = false) => {
    const box = boxRef.current.getBoundingClientRect();
    const cx = px - box.left - box.width / 2; const cy = py - box.top - box.height / 2;
    const { s, x, y } = v.current;
    const next = Math.min(MAX, Math.max(1, s2));
    const k = next / s;
    setAnim(smooth);
    setView(next === 1 ? { s: 1, x: 0, y: 0 } : clamp(next, cx - (cx - x) * k, cy - (cy - y) * k));
  };

  const onPointerDown = (e) => {
    boxRef.current.setPointerCapture?.(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const pts = [...pointers.current.values()];
    if (pts.length === 2) {
      const d = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
      gesture.current = { kind: 'pinch', d, s: v.current.s, mx: (pts[0].x + pts[1].x) / 2, my: (pts[0].y + pts[1].y) / 2, x: v.current.x, y: v.current.y };
    } else if (pts.length === 1) {
      gesture.current = { kind: 'pan', sx: e.clientX, sy: e.clientY, x: v.current.x, y: v.current.y, moved: false, onImg: e.target === imgRef.current };
    }
  };
  const onPointerMove = (e) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const g = gesture.current; if (!g) return;
    setAnim(false);
    if (g.kind === 'pinch' && pointers.current.size >= 2) {
      const pts = [...pointers.current.values()];
      const d = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
      const s2 = Math.min(MAX, Math.max(1, g.s * (d / g.d)));
      const box = boxRef.current.getBoundingClientRect();
      const cx = g.mx - box.left - box.width / 2; const cy = g.my - box.top - box.height / 2;
      const mx = (pts[0].x + pts[1].x) / 2; const my = (pts[0].y + pts[1].y) / 2;
      const k = s2 / g.s;
      setView(clamp(s2, cx - (cx - g.x) * k + (mx - g.mx), cy - (cy - g.y) * k + (my - g.my)));
    } else if (g.kind === 'pan') {
      const dx = e.clientX - g.sx; const dy = e.clientY - g.sy;
      if (Math.abs(dx) + Math.abs(dy) > 6) g.moved = true;
      if (v.current.s > 1) setView(clamp(v.current.s, g.x + dx, g.y + dy));
      else setView({ s: 1, x: 0, y: Math.max(0, dy) });     // swipe down to close
    }
  };
  const onPointerUp = (e) => {
    pointers.current.delete(e.pointerId);
    const g = gesture.current;
    if (pointers.current.size > 0) {           // one finger lifted from a pinch → keep panning with the other
      const [p] = [...pointers.current.values()];
      gesture.current = { kind: 'pan', sx: p.x, sy: p.y, x: v.current.x, y: v.current.y, moved: true, onImg: true };
      return;
    }
    gesture.current = null;
    if (!g || g.kind !== 'pan') return;
    if (v.current.s === 1 && v.current.y > 110) { onClose(); return; }      // swiped down
    if (v.current.s === 1 && v.current.y) { setAnim(true); setView({ s: 1, x: 0, y: 0 }); }
    if (g.moved) return;
    const now = Date.now();
    const lt = lastTap.current;
    if (now - lt.t < 300 && Math.hypot(e.clientX - lt.x, e.clientY - lt.y) < 30) {
      lastTap.current = { t: 0, x: 0, y: 0 };
      zoomAt(v.current.s > 1 ? 1 : 2.5, e.clientX, e.clientY, true);        // double-tap
      return;
    }
    lastTap.current = { t: now, x: e.clientX, y: e.clientY };
    // a single tap on the dark area closes (only when not zoomed)
    if (!g.onImg && v.current.s === 1) setTimeout(() => { if (lastTap.current.t === now) onClose(); }, 300);
  };
  const onWheel = (e) => {
    e.preventDefault();
    zoomAt(v.current.s * (e.deltaY < 0 ? 1.15 : 1 / 1.15), e.clientX, e.clientY);
  };
  useEffect(() => {
    const el = boxRef.current;
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }); // eslint-disable-line react-hooks/exhaustive-deps

  const fade = view.s === 1 ? Math.max(0.35, 1 - view.y / 400) : 1;
  return (
    <div role="dialog" aria-modal="true" className="fixed inset-0 z-[100]" style={{ background: `rgba(0,0,0,${0.92 * fade})` }}>
      <div
        ref={boxRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        className="absolute inset-0 flex items-center justify-center overflow-hidden select-none"
        style={{ touchAction: 'none', cursor: view.s > 1 ? 'grab' : 'zoom-in' }}
      >
        <img
          ref={imgRef}
          src={src}
          alt=""
          draggable={false}
          className="max-w-full max-h-full object-contain"
          style={{
            transform: `translate(${view.x}px, ${view.y}px) scale(${view.s})`,
            transition: anim ? 'transform .22s ease-out' : 'none',
            willChange: 'transform', padding: 8, boxSizing: 'border-box',
          }}
        />
      </div>
      <button
        type="button"
        onClick={onClose}
        aria-label={t('survey.close', 'Close')}
        className="fixed top-3 right-3 w-11 h-11 rounded-full bg-white/90 text-gray-900 text-xl font-bold shadow"
        style={{ zIndex: 101 }}
      >
        ✕
      </button>
      {view.s === 1 && (
        <div className="fixed bottom-4 left-0 right-0 text-center pointer-events-none" style={{ zIndex: 101 }}>
          <span className="inline-block rounded-full bg-white/15 text-white/90 text-xs px-3 py-1.5">
            {t('survey.zoomHint', 'Double-tap to zoom · pinch to enlarge')}
          </span>
        </div>
      )}
    </div>
  );
}
